import { useEffect, useRef, useState } from 'react';

// Just past the CSS enter animation's duration (theme/motion.ts
// durations.base, 200ms) — long enough for the one-shot keyframe to finish
// before an id falls out of the returned set. If list virtualization
// remounts a row after this window (the user scrolled it out and back), it
// renders with no animation, since by then the id is no longer "new".
const ANIMATE_WINDOW_MS = 260;

/**
 * Tracks which ids in a list are newly inserted ABOVE the items already on
 * screen — e.g. a live-update post, or one the user just created — so a
 * list can play a one-shot CSS enter animation on those rows only.
 *
 * Deliberately narrow:
 * - Nothing animates on the first (or first non-empty) render — those are
 *   the list's starting contents, not insertions.
 * - An id already seen never re-animates, so a re-render that only changes
 *   an existing item (e.g. a like toggle producing a new array reference
 *   with the same ids) animates nothing.
 * - Only items ABOVE the first previously-seen id count as "inserted" —
 *   pagination appends unseen ids at the bottom, which this treats the same
 *   as everything else and leaves alone.
 * - If NO item in the new list was seen before (e.g. switching feed filters
 *   to a tab never viewed, or a wholesale list swap), nothing animates —
 *   there's no "top" to insert above, so this refuses to guess and instead
 *   treats it as a fresh baseline.
 * - Returned ids expire after ANIMATE_WINDOW_MS so a later virtualization
 *   remount of the same row (same `items` reference, so this hook never
 *   re-runs for it) doesn't replay the animation.
 *
 * `getId` should be a referentially stable function (e.g. a module-level
 * `(item) => item.id`) — it's a dependency of the effect that walks `items`.
 */
export function useNewItemIds<T>(items: T[], getId: (item: T) => string): Set<string> {
  const seenIds = useRef<Set<string>>(new Set());
  const hasBaseline = useRef(false);
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(() => new Set());
  // Expiry timers live outside the effect's cleanup: `items` often changes
  // again within the window (an optimistic post, then its server copy), and
  // cancelling the timer there would leave the id "new" forever.
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const t of pending) clearTimeout(t);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    if (!hasBaseline.current) {
      // Wait for the first non-empty render to draw the baseline — an
      // empty-then-populated list (the common loading sequence) would
      // otherwise treat every first-page item as "new".
      if (items.length === 0) return;
      seenIds.current = new Set(items.map(getId));
      hasBaseline.current = true;
      return;
    }

    const firstKnownIndex = items.findIndex((item) => seenIds.current.has(getId(item)));
    // No overlap with anything seen before: not an insertion into the list
    // the viewer already has, but a different list entirely. Adopt it as a
    // silent new baseline rather than animating the whole thing in.
    const boundary = firstKnownIndex === -1 ? 0 : firstKnownIndex;

    const newlyInserted: string[] = [];
    for (let i = 0; i < boundary; i++) {
      const id = getId(items[i]);
      if (!seenIds.current.has(id)) newlyInserted.push(id);
    }

    for (const item of items) seenIds.current.add(getId(item));

    if (newlyInserted.length === 0) return;

    setAnimatingIds((prev) => {
      const next = new Set(prev);
      for (const id of newlyInserted) next.add(id);
      return next;
    });

    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setAnimatingIds((prev) => {
        if (newlyInserted.every((id) => !prev.has(id))) return prev;
        const next = new Set(prev);
        for (const id of newlyInserted) next.delete(id);
        return next;
      });
    }, ANIMATE_WINDOW_MS);
    timers.current.add(timer);
  }, [items, getId]);

  return animatingIds;
}
